import { ReactNode, forwardRef } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  controlWrapper,
  disabled,
  errorBorder,
  errorText,
  input,
  inputWrapper,
  label,
  supportingText,
  textDisabled,
  notDisabled,
} from './input.css';
import { Typography } from '../typography/typography';
import { clsx } from 'clsx';
import { sprinkles } from '@design-system/sprinkles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';

export type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'min' | 'max'
> & {
  startIcon?: {
    icon: IconProp;
    onClick?: () => void;
    className?: string;
  };
  endIcon?: {
    icon: IconProp;
    onClick?: () => void;
    className?: string;
  };
  label?: string;
  error?: string;
  hasError?: boolean;
  supportingText?: string;
  testId?: string;
  endCta?: ReactNode;
  wrapperClassName?: string;
  onChange: (value: string) => void;
};
const omitKeys: Array<keyof InputProps> = [
  'startIcon',
  'endIcon',
  'supportingText',
  'testId',
  'endCta',
  'onChange',
  'hasError',
];

export const InputPrice = forwardRef<HTMLInputElement, InputProps>((p, ref) => {
  const onChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = ev.target.value;

    if (inputValue.endsWith(',')) {
      inputValue = inputValue.replace(',', '.');
    }

    inputValue = inputValue.replace(/[^0-9.]/g, '');
    inputValue = inputValue.replace(/\.{,}/g, '.');

    const [, decimal] = inputValue.split('.');
    if (decimal && decimal.length > 2) {
      return;
    }
    p.onChange(inputValue);
  };
  return (
    <div className={clsx(controlWrapper, p?.wrapperClassName)}>
      {p?.label ? (
        <div className={sprinkles({ display: 'flex' })}>
          <Typography
            variant="body"
            size="medium"
            className={clsx(
              label,
              (p?.error || p?.hasError) && errorText,
              p?.disabled && textDisabled,
            )}
          >
            {p.label}
          </Typography>
          {p?.required ? (
            <Typography
              variant="body"
              size="medium"
              className={clsx(
                errorText,
                sprinkles({ paddingTop: 's' }),
                p?.disabled && textDisabled,
              )}
            >
              *
            </Typography>
          ) : null}
        </div>
      ) : null}
      <div
        className={clsx(
          inputWrapper,
          (p?.error || p?.hasError) && errorBorder,
          !p?.disabled && notDisabled,
          p?.disabled && disabled,
        )}
      >
        {p?.startIcon ? (
          <FontAwesomeIcon
            data-testid="start-icon"
            icon={p.startIcon.icon}
            className={clsx(p?.startIcon?.className)}
            onClick={() => p?.startIcon?.onClick && p?.startIcon?.onClick()}
          />
        ) : null}
        <input
          ref={ref}
          {..._.omit(p, omitKeys)}
          value={p.value}
          onChange={onChange}
          role="input"
          name="ds-input"
          onFocus={(e) => e.target.select()}
          className={clsx(input, 'inputNumber')}
          data-testid={p?.testId}
        />
        {p?.endIcon ? (
          <FontAwesomeIcon
            data-testid="end-icon"
            icon={p.endIcon.icon}
            className={clsx(p?.endIcon?.className)}
            onClick={() => p?.endIcon?.onClick && p?.endIcon?.onClick()}
          />
        ) : null}
        {p?.endCta ? p.endCta : null}
      </div>
      {p?.supportingText || p?.error ? (
        <Typography
          variant="body"
          size="small"
          className={clsx(
            supportingText,
            (p?.error || p?.hasError) && errorText,
          )}
        >
          {p.error || p.supportingText}
        </Typography>
      ) : null}
    </div>
  );
});
